import {
  getMatchResults,
  Match,
  matchCompleteByByesOrForfeits,
  matchContainsRoster,
} from "model/Match/Match";
import { MatchmakingSet } from "model/Match/Set/Matchmaking/MatchmakingSet";
import {
  getDefinedRosterIds,
  getRoster,
  isForfeited,
} from "model/Roster/Roster";
import { getSumOfOtherScores } from "model/Scores";

const MUJIKI_ROSTER_ID = "391";

export type OmniumSeasonRecord = {
  rosterId: string;
  points: number;
  position: number | undefined;
  wins: number;
  draws: number;
  loses: number;
  setPoints: number;
  trackScoreAgainst: number;
  trackScoreFor: number;
  rosterIdsBeaten: string[];
};

export type OmniumSeasonRecordWithDisplay = {
  record: OmniumSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createOmniumSeasonRecord = (rosterId: string): OmniumSeasonRecord => {
  return {
    rosterId,
    points: 0,
    position: undefined,
    wins: 0,
    draws: 0,
    loses: 0,
    setPoints: 0,
    trackScoreAgainst: 0,
    trackScoreFor: 0,
    rosterIdsBeaten: [],
  };
};

export const calculateOmniumSeasonRecord = (
  rosterIds: string[],
  matchesByPlayday: Match[][]
) => {
  const records = new Map<string, OmniumSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createOmniumSeasonRecord(id));
  });

  for (let playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (let matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      const matchResults = getMatchResults(match);
      if (!matchResults) {
        continue;
      }

      // todo - HARDCODED FIX FOR MUJIKI DEF WINS (to be configed)
      if (matchContainsRoster(match, MUJIKI_ROSTER_ID, "Squad")) {
        const mujikiRecord = records.get(MUJIKI_ROSTER_ID);
        if (!!mujikiRecord) {
          mujikiRecord.loses++;
        }

        const otherRosterId = getDefinedRosterIds(match.teams.rosters).find(
          (id) => id !== MUJIKI_ROSTER_ID
        );
        const otherRecord = !!otherRosterId
          ? records.get(otherRosterId)
          : undefined;
        if (!!otherRecord) {
          otherRecord.wins++;
          otherRecord.setPoints = (otherRecord.setPoints ?? 0) + 3;
        }

        continue;
      }

      for (let rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const rosterRecord = records.get(rosterId);
        const rosterResult = matchResults[rosterId];
        if (matchCompleteByByesOrForfeits(match.teams.rosters)) {
          const roster = getRoster(match.teams.rosters, rosterId);
          if (!roster || !rosterRecord) {
            continue;
          }

          const forfeiter = isForfeited(roster);
          if (forfeiter) {
            rosterRecord.loses++;
          } else {
            rosterRecord.wins++;
            rosterRecord.trackScoreFor = (rosterRecord.trackScoreFor ?? 0) + 24;
            rosterRecord.setPoints = (rosterRecord.setPoints ?? 0) + 3;
          }

          continue;
        }

        if (!rosterRecord || !rosterResult) {
          continue;
        }

        let trackPointsFor = 0;
        let trackPointsAgainst = 0;
        if (rosterResult === "W") {
          rosterRecord.rosterIdsBeaten.push(
            ...getDefinedRosterIds(match.teams.rosters).filter(
              (id) => id !== rosterId
            )
          );
        }

        for (let setI = 0; setI < match.sets.length; setI++) {
          const set = match.sets[setI] as MatchmakingSet;
          for (let trackI = 0; trackI < set.tracks.length; trackI++) {
            const track = set.tracks[trackI];
            trackPointsFor += track.scores[rosterId];
            trackPointsAgainst += getSumOfOtherScores(track.scores, rosterId);
          }
        }

        if (rosterResult === "W") {
          rosterRecord.wins++;
        } else if (rosterResult === "D") {
          rosterRecord.draws++;
        } else if (rosterResult === "L") {
          rosterRecord.loses++;
        }

        rosterRecord.trackScoreAgainst += trackPointsAgainst;
        rosterRecord.trackScoreFor += trackPointsFor;
        if (!!match.scores) {
          rosterRecord.setPoints += match.scores[rosterId] ?? 0;
        }
      }
    }
  }

  const recordValues = Array.from(records.values());
  recordValues.forEach((rv) => {
    rv.points = rv.wins * 3 + rv.draws * 1;
  });

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0 || sortedRecords[0].points === 0) {
    return sortedRecords;
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (sortedRecords[i].points === undefined) {
      break;
    }

    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (
  a: OmniumSeasonRecord,
  b: OmniumSeasonRecord
): number => {
  if (a.points !== b.points) {
    return b.points - a.points;
  }

  if (a.rosterIdsBeaten.includes(b.rosterId)) {
    return -1;
  }

  if (b.rosterIdsBeaten.includes(a.rosterId)) {
    return 1;
  }

  const aRoundsDifferential = a.trackScoreFor - a.trackScoreAgainst;
  const bRoundsDifferential = b.trackScoreFor - b.trackScoreAgainst;
  if (aRoundsDifferential !== bRoundsDifferential) {
    return bRoundsDifferential - aRoundsDifferential;
  }

  return b.setPoints - a.setPoints;
};
